import React, { useRef, useState } from 'react' // ✅ Adicionar useState
import { IModalProps } from 'context/ModalContext'
import { ReactComponent as AlertIcon } from 'assets/icons/mov-alert.svg'
import { ReactComponent as ErrorIcon } from 'assets/icons/mov-error.svg'
import {
  TableCellStyled,
  Button,
  TableBodyStyled,
  TableContainerStyled,
  TableHeadStyled,
  TableStyled,
  TableRowStyled,
  TableFooter,
  Line
} from 'components'
import {
  DraggableDialog,
  resetModalState
} from 'components/DraggableDialog/DraggableDialog'
import { ExcelIcon } from 'components/Svg'
import { ErrorList } from 'pages/Supplier/MovMensal/types'
import Draggable from 'react-draggable'
import { FORMAT } from 'utils'

import {
  DialogActionsStyled,
  DialogContentStyled,
  DialogTitleStyled
} from './ModalRelatorioErros.styles'
import { useContextSelector } from 'use-context-selector'
import { ModalContext } from 'context/ModalContext'


export interface IModalRelatorioErros {
  showModal?: boolean
  toggleModal?: () => void
  errorList?: ErrorList[] | undefined
  autonomo?: boolean
  onSimClick?: () => void,
  onNaoClick?: () => void
}

const ModalRelatorioErros: React.FC<IModalRelatorioErros> = ({
  showModal,
  toggleModal,
  errorList = [],
  autonomo,
  onSimClick,
  onNaoClick
}) => {
  const [page, setPage] = React.useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = React.useState<number>(15)

  // ✅ Adicionar os estados que estavam faltando
  const [isProcessing, setIsProcessing] = useState(false)
  const [connectionError, setConnectionError] = useState(false)

  const handleConfirmModalRelatorioErro = useContextSelector(ModalContext, (state: IModalProps) => state.handleConfirm)

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }
  const draggableReference = useRef<Draggable>(null)

  const handleSimClickWithRetry = async () => {
    setIsProcessing(true);
    setConnectionError(false);

    try {
      // ✅ Usar a função retryOperation que está fora do componente
      await retryOperation(async () => {
        if (onSimClick) {
          await onSimClick();
        }
      }, 3); // 3 tentativas

      toggleModal?.(); // ✅ Adicionar optional chaining
      resetModalState(draggableReference);
    } catch (error) {
      setConnectionError(true);
      // Não fechar o modal em caso de erro
      console.error('Erro ao processar operação:', error);
    } finally {
      setIsProcessing(false);
    }
  };


  return (
    <DraggableDialog
      reference={draggableReference}
      showModal={showModal}
      onClose={!isProcessing ? toggleModal : undefined} // Impedir fechamento durante processamento
      width="lg"
    >
      <Line />
      <DialogTitleStyled>Relatório Alertas / Erros</DialogTitleStyled>
      <DialogContentStyled>
        <TableContainerStyled>
          <TableStyled>
            <TableHeadStyled>
              <TableRowStyled>
                <TableCellStyled
                  width={200}
                  padding="checkbox"
                  align="center"
                />
                <TableCellStyled maxwidth="15rem">
                  CPF/Passaporte
                </TableCellStyled>
                <TableCellStyled>Nome</TableCellStyled>
                <TableCellStyled>Mensagem</TableCellStyled>
              </TableRowStyled>
            </TableHeadStyled>
            <TableBodyStyled>
              {(rowsPerPage > 0
                ? errorList?.slice(
                  page * rowsPerPage,
                  page * rowsPerPage + rowsPerPage
                )
                : errorList
              )?.map((el, index) => (
                <TableRowStyled key={el.toString() + index}>
                  <TableCellStyled padding="checkbox" align="center">
                    {el.flError ? <ErrorIcon /> : <AlertIcon />}
                  </TableCellStyled>
                  <TableCellStyled>
                    {FORMAT.documento(el.cdCpfPassaporte)}
                  </TableCellStyled>
                  <TableCellStyled>{el.name}</TableCellStyled>
                  <TableCellStyled>{el.message}</TableCellStyled>
                </TableRowStyled>
              ))}
            </TableBodyStyled>
          </TableStyled>
        </TableContainerStyled>
        <TableFooter
          typeFooter="offline"
          page={page}
          rowsPerPage={rowsPerPage}
          rows={errorList}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
        {/* ✅ Mover o erro para dentro do DialogContentStyled */}
        {connectionError && (
          <div style={{ color: 'red', padding: '10px', textAlign: 'center' }}>
            ⚠️ Erro de conectividade. Tente novamente.
          </div>
        )}
      </DialogContentStyled>
      <DialogActionsStyled>
        {autonomo ? (
          <div>
            <Button
              text={isProcessing ? "Processando..." : "Sim"}
              width='6rem'
              disabled={isProcessing}
              onClick={handleSimClickWithRetry}
            />
            <Button
              text="Não"
              width='6rem'
              marginLeft='1rem'
              marginRight='0.5rem'
              disabled={isProcessing}
              onClick={() => {
                if (!isProcessing) {
                  toggleModal?.(); // ✅ Adicionar optional chaining
                  onNaoClick && onNaoClick();
                  resetModalState(draggableReference);
                }
              }}
            />
          </div>
        ) : (
          <Button
            text="Fechar"
            disabled={isProcessing}
            onClick={() => {
              if (!isProcessing) {
                toggleModal?.(); // ✅ Adicionar optional chaining
                resetModalState(draggableReference);
              }
            }}
          />
        )}
      </DialogActionsStyled>
    </DraggableDialog>
  )
}

// Função de retry
const retryOperation = async (operation: () => Promise<void>, maxRetries: number) => {
  for (let i = 0; i < maxRetries; i++) {
    try {
      await operation();
      return; // Sucesso
    } catch (error) {
      if (i === maxRetries - 1) {
        throw error; // Última tentativa falhou
      }
      // Aguardar antes da próxima tentativa
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

export default ModalRelatorioErros